import { useCallback, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  SignupKeysService,
  UsersService,
  type SignupKeyCreatedResponse,
  type SignupKeyOut,
  type UserListFilters,
  type UserListResponse,
  type UserOut,
  type UserRole,
  type UserStatus,
} from "@/api";
import { useApi } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { formatDateID, formatRelativeTimeID } from "@/lib/format";
import { Alert } from "@/components/ui/alert";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FilterChips } from "@/components/shared/filter-chip";
import { Pagination } from "@/components/shared/pagination";
import { Section } from "@/components/shared/section";
import { InviteCreateDialog } from "@/components/settings/invite-create-dialog";
import { InviteRevokeDialog } from "@/components/settings/invite-revoke-dialog";
import { SignupKeyShowOnceDialog } from "@/components/settings/signup-key-show-once-dialog";

const PAGE_SIZE = 25;
const TENANT_ADMIN_ROLES = new Set(["admin", "super_admin"]);

// Role / status identifiers map to translation keys so badges
// and filter chips re-render on a locale switch.
const ROLE_LABEL_KEY: Record<UserRole | string, TranslationKey> = {
  super_admin: "settings.roles.super_admin",
  admin: "settings.roles.admin",
  member: "settings.roles.member",
  viewer: "settings.roles.viewer",
  partner_admin: "settings.roles.partner_admin",
};

const ROLE_TONE: Record<string, BadgeTone> = {
  super_admin: "danger",
  admin: "ocean",
  member: "neutral",
  viewer: "info",
  partner_admin: "warning",
};

const STATUS_LABEL_KEY: Record<UserStatus, TranslationKey> = {
  active: "settings.userStatus.active",
  suspended: "settings.userStatus.suspended",
  deleted: "settings.userStatus.deleted",
};

const STATUS_TONE: Record<UserStatus, BadgeTone> = {
  active: "success",
  suspended: "warning",
  deleted: "danger",
};

const FILTER_ROLE_VALUES: ReadonlyArray<UserRole> = [
  "admin",
  "member",
  "viewer",
];

const FILTER_STATUS_VALUES: ReadonlyArray<UserStatus> = [
  "active",
  "suspended",
];

export default function SettingsUsersTab() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const canManage = user ? TENANT_ADMIN_ROLES.has(user.role) : false;

  return (
    <div className="space-y-8">
      {!canManage && (
        <Alert variant="info">{t("settings.users.readOnlyNotice")}</Alert>
      )}
      <UsersListSection canManage={canManage} />
      {canManage && <InvitesSection />}
    </div>
  );
}

function UsersListSection({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<UserStatus | null>(null);
  const [roleFilter, setRoleFilter] = useState<UserRole | null>(null);
  const [page, setPage] = useState(0);

  const roleOptions = useMemo(
    () =>
      FILTER_ROLE_VALUES.map((r) => ({
        value: r,
        label: t(ROLE_LABEL_KEY[r]),
      })),
    [t]
  );
  const statusOptions = useMemo(
    () =>
      FILTER_STATUS_VALUES.map((s) => ({
        value: s,
        label: t(STATUS_LABEL_KEY[s]),
      })),
    [t]
  );

  const offset = page * PAGE_SIZE;
  const fetcher = useCallback(() => {
    const filters: UserListFilters = { limit: PAGE_SIZE, offset };
    if (statusFilter) filters.status = statusFilter;
    if (roleFilter) filters.role = roleFilter;
    return UsersService.list(filters);
  }, [statusFilter, roleFilter, offset]);

  const { data, error, isLoading } = useApi<UserListResponse>(fetcher, {
    deps: [statusFilter, roleFilter, offset],
  });

  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const hasNext = offset + items.length < total;

  // Filter changes reset to page 0 so the visible slice always
  // reflects the new query envelope.
  const handleStatus = (next: UserStatus | null) => {
    setStatusFilter(next);
    setPage(0);
  };
  const handleRole = (next: UserRole | null) => {
    setRoleFilter(next);
    setPage(0);
  };

  return (
    <Section
      title={t("settings.users.list.title")}
      description={
        canManage
          ? t("settings.users.list.descriptionManage")
          : t("settings.users.list.descriptionReadOnly")
      }
    >
      <div className="space-y-3">
        {error && <Alert variant="warning">{parseApiError(error)}</Alert>}

        <div className="flex flex-col gap-2 tablet:flex-row tablet:items-center">
          <FilterChips
            label={t("settings.users.list.filterStatusLabel")}
            options={statusOptions}
            value={statusFilter}
            onChange={handleStatus}
          />
          <FilterChips
            label={t("settings.users.list.filterRoleLabel")}
            options={roleOptions}
            value={roleFilter}
            onChange={handleRole}
          />
        </div>

        <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("settings.users.list.colName")}</TableHead>
                <TableHead>{t("settings.users.list.colEmail")}</TableHead>
                <TableHead>{t("settings.users.list.colRole")}</TableHead>
                <TableHead>{t("settings.users.list.colStatus")}</TableHead>
                <TableHead className="hidden tablet:table-cell">
                  {t("settings.users.list.colLastLogin")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && items.length === 0 && (
                <>
                  {[0, 1, 2].map((i) => (
                    <TableRow key={`u-sk-${i}`}>
                      <TableCell colSpan={5} className="py-4">
                        <Skeleton className="h-5 w-full" />
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}
              {!isLoading && items.length === 0 && (
                <TableEmpty
                  colSpan={5}
                  message={t("settings.users.list.emptyFiltered")}
                />
              )}
              {items.map((u) => (
                <UserRowView key={u.id} user={u} />
              ))}
            </TableBody>
          </Table>
        </div>

        {items.length > 0 && (
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            hasNext={hasNext}
            onPrev={() => setPage((p) => Math.max(0, p - 1))}
            onNext={() => setPage((p) => p + 1)}
          />
        )}
      </div>
    </Section>
  );
}

function UserRowView({ user }: { user: UserOut }) {
  const { t } = useTranslation();
  const roleLabel = ROLE_LABEL_KEY[user.role]
    ? t(ROLE_LABEL_KEY[user.role])
    : user.role;
  return (
    <TableRow>
      <TableCell className="font-medium text-ocean-900">
        {user.full_name || (
          <span className="text-neutral-400">
            {t("settings.users.list.emptyValue")}
          </span>
        )}
      </TableCell>
      <TableCell className="text-neutral-700">{user.email}</TableCell>
      <TableCell>
        <Badge tone={ROLE_TONE[user.role] ?? "neutral"}>{roleLabel}</Badge>
      </TableCell>
      <TableCell>
        <Badge tone={STATUS_TONE[user.status]}>
          {t(STATUS_LABEL_KEY[user.status])}
        </Badge>
      </TableCell>
      <TableCell className="hidden tablet:table-cell text-xs text-neutral-600">
        {user.last_login_at
          ? formatRelativeTimeID(user.last_login_at)
          : t("settings.users.list.neverLoggedIn")}
      </TableCell>
    </TableRow>
  );
}

/** Mirror of ``PILOT_MAX_ACTIVE_SIGNUP_KEYS_PER_TENANT`` from
 *  ``apps/api/src/signup_keys/service.py``. Keeping the constant
 *  on both sides means the counter copy stays in sync without an
 *  extra round-trip. If the backend bumps the limit, update here
 *  too. */
const PILOT_MAX_ACTIVE_SIGNUP_KEYS = 5;

function InvitesSection() {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [showOnce, setShowOnce] = useState<SignupKeyCreatedResponse | null>(
    null
  );
  const [revokeTarget, setRevokeTarget] = useState<SignupKeyOut | null>(null);

  const fetcher = useCallback(
    () => SignupKeysService.list({ include_used: false, include_revoked: false }),
    []
  );
  const { data, error, isLoading, refetch } = useApi<SignupKeyOut[]>(fetcher);

  const items = data ?? [];
  // Backend treats expired keys as inactive too — mirror that
  // here so the counter never gets out of sync with the server.
  const now = Date.now();
  const activeCount = items.filter(
    (inv) =>
      inv.revoked_at === null &&
      inv.used_at === null &&
      new Date(inv.expires_at).getTime() > now
  ).length;
  const remainingSlots = Math.max(0, PILOT_MAX_ACTIVE_SIGNUP_KEYS - activeCount);
  const quotaReached = remainingSlots === 0;

  return (
    <Section
      title={t("settings.users.invites.title")}
      description={t("settings.users.invites.description")}
      action={
        <Button
          onClick={() => setCreateOpen(true)}
          size="sm"
          disabled={quotaReached}
          aria-disabled={quotaReached}
        >
          <Plus size={14} />
          {t("settings.users.invites.inviteButton")}
        </Button>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-col gap-1 rounded-md border border-ocean-100 bg-ocean-50/40 px-3 py-2 text-xs text-ocean-800 tablet:flex-row tablet:items-center tablet:justify-between">
          <span>
            {t("settings.users.invites.quotaUsage", {
              active: activeCount,
              max: PILOT_MAX_ACTIVE_SIGNUP_KEYS,
            })}
            {!quotaReached &&
              t("settings.users.invites.quotaRemaining", {
                remaining: remainingSlots,
              })}
          </span>
          {quotaReached && (
            <span className="font-medium text-warning-600">
              {t("settings.users.invites.quotaReachedBadge")}
            </span>
          )}
        </div>
        {quotaReached && (
          <Alert variant="warning">
            {t("settings.users.invites.quotaReachedAlert")}
          </Alert>
        )}
        {error && <Alert variant="warning">{parseApiError(error)}</Alert>}

        <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t("settings.users.invites.colPrefix")}
                </TableHead>
                <TableHead>{t("settings.users.invites.colRole")}</TableHead>
                <TableHead className="hidden tablet:table-cell">
                  {t("settings.users.invites.colNote")}
                </TableHead>
                <TableHead>
                  {t("settings.users.invites.colExpires")}
                </TableHead>
                <TableHead className="text-right">
                  {t("settings.users.invites.colActions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && items.length === 0 && (
                <>
                  {[0, 1].map((i) => (
                    <TableRow key={`inv-sk-${i}`}>
                      <TableCell colSpan={5} className="py-4">
                        <Skeleton className="h-5 w-full" />
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}
              {!isLoading && items.length === 0 && (
                <TableEmpty
                  colSpan={5}
                  message={t("settings.users.invites.emptyList")}
                />
              )}
              {items.map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell className="font-mono text-xs text-ocean-700">
                    {inv.display_prefix}…
                  </TableCell>
                  <TableCell>
                    <Badge tone={ROLE_TONE[inv.default_role] ?? "neutral"}>
                      {ROLE_LABEL_KEY[inv.default_role]
                        ? t(ROLE_LABEL_KEY[inv.default_role])
                        : inv.default_role}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden tablet:table-cell text-xs text-neutral-600">
                    {inv.note || (
                      <span className="text-neutral-400">
                        {t("settings.users.invites.emptyValue")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-neutral-700">
                    {formatDateID(inv.expires_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setRevokeTarget(inv)}
                      aria-label={t("settings.users.invites.revokeAria", {
                        prefix: inv.display_prefix,
                      })}
                    >
                      <Trash2 size={14} />
                      {t("settings.users.invites.revokeButton")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <InviteCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(key) => {
          setCreateOpen(false);
          setShowOnce(key);
          void refetch();
        }}
      />

      <SignupKeyShowOnceDialog
        keyData={showOnce}
        onClose={() => setShowOnce(null)}
      />

      <InviteRevokeDialog
        invite={revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onRevoked={() => {
          setRevokeTarget(null);
          void refetch();
        }}
      />
    </Section>
  );
}
